import React, { useEffect, useRef, useState, useCallback } from "react";
import { useRouteMatch, useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Button,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Divider,
  Typography,
  Hidden,
  useTheme,
  useMediaQuery,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import ImageOutlinedIcon from "@material-ui/icons/ImageOutlined";
import VisibilityIcon from "@material-ui/icons/Visibility";
import { Icon } from "@iconify/react";
import _ from "lodash";

import MenuAddModal from "../../MenusNewTest/Modal/MenuAddModal";
import MenuEditModal from "../../MenusNewTest/Modal/MenuEditModal";
import { CustomTheme } from "../../../../../types/customTheme";
import { ERROR_MESSAGE_UNEXPECTED_ERROR } from "../../../../../utils/consts";
import ButtonCommon from "../../../../../components/buttons/ButtonCommon";
import PageHeaderMobile from "../../../../../components/common/PageHeader/PageHeaderMobile";
import TextSkeletonLoading from "../../../../../components/common/Loading/TextSkeletonLoading";
import Authorities from "../../../../../auth/authorities";
import withAuthority from "../../../../../components/Auth/withAuthority";
import DefaultAlert from "../../../../../components/alerts/DefaultAlert";
import {
  fetchMenus,
  updateMenu,
  fetchAllMenuDetails,
  setProductsVersion,
} from "../../../../../redux/actions/menu";
import MenuListSkeletonLoading from "./MenuListSkeletonLoading";
import { fetchProductConfigInfo } from "../../../../../services/menuApp/printerMenuService";
import { AxiosError } from "axios";

const MenuList: React.FunctionComponent = () => {
  const [error, setError] = useState("");
  const [isOpenMenuAddModal, setIsOpenMenuAddModal] = useState(false);
  const [isOpenMenuEditModal, setIsOpenMenuEditModal] = useState(false);
  const [menuName, setMenuName] = useState("");
  const [menu, setMenu] = useState<any>({});
  const [isMenuNameEmpty, setIsMenuNameEmpty] = useState(false);
  const [isLoadingButton, setIsLoadingButton] = useState(false);
  const [menuNameInitial, setMenuNameInitial] = useState("");
  const [type, setType] = useState("");
  const [brand, setBrand] = useState("");
  const [typeInitial, setTypeInitial] = useState("");
  const [brandInitial, setBrandInitial] = useState("");
  const [updatingMenuId, setUpdatingMenuId] = useState<number | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const productsVersion = useSelector(
    (state: any) => state.menu?.productsVersion || 1,
  );
  const isEditDisabled = productsVersion === 1;

  const match: any = useRouteMatch();
  const locationId = match.params.locationId;

  const history = useHistory();
  const theme: CustomTheme = useTheme();
  const dispatch = useDispatch();

  // Use individual selectors to avoid unwanted re-renders
  const menus = useSelector((state: any) => state.menu?.menus || []);
  const categoriesInfo = useSelector(
    (state: any) => state.menu?.categoriesInfo || {},
  );
  const isLoading = useSelector((state: any) => state.menu?.isLoading || false);

  // Use a ref to track if initial load has been attempted
  const hasLoadedRef = useRef(false);

  // Debug: Log menus changes
  useEffect(() => {
    console.log("Menus changed in component, length:", menus?.length);
    console.log("First menu valid status:", menus?.[0]?.valid);
  }, [menus, refreshTrigger]);

  // Initial load - fetch all menus and all menu details at once
  useEffect(() => {
    const loadAllData = async () => {
      // Always fetch if menus are empty, regardless of isInitialLoad
      if (!menus || menus.length === 0) {
        console.log("Fetching all menus and details (menus empty)");
        await dispatch(fetchMenus(match.params.locationId));
        await dispatch(fetchAllMenuDetails(match.params.locationId));
        await getProductConfiguration();
      } else if (!hasLoadedRef.current) {
        // If menus exist but product config not fetched yet
        console.log("Menus exist, fetching product config");
        await getProductConfiguration();
        hasLoadedRef.current = true;
      }
    };

    loadAllData();
  }, [match.params.locationId]);

  // ✅ When component comes back into focus, refresh data
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log("Page became visible, refreshing menu data");
        dispatch(fetchMenus(match.params.locationId));
        dispatch(fetchAllMenuDetails(match.params.locationId));
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [dispatch, match.params.locationId]);

  const getProductConfiguration = async () => {
    if (!locationId) return;

    try {
      const { data } = await fetchProductConfigInfo(locationId);
      if (data && data.data) {
        const version = data.data.productsVersion || 1;
        // Dispatch to Redux
        dispatch(setProductsVersion(version));
      }
    } catch (error) {
      const err: any = error as AxiosError;
      if (err.response?.status !== 404) {
        console.error("Error fetching product configuration:", error);
      }
    } finally {
    }
  };

  const handleViewMenu = (
    e: React.MouseEvent,
    menuId: string,
    menuName: string,
  ) => {
    e.stopPropagation();
    // Navigation only - no API call needed because data is already in Redux
    history.push(`${match.url}/${menuId}`);
  };

  const handleDeactivateMenu = async (nodeData: any, valid: any) => {
    const cloneData = _.cloneDeep(nodeData);
    const newValid = !valid;
    cloneData.valid = newValid;

    setUpdatingMenuId(nodeData.id);

    // Create log body for menu deactivation/activation
    const logBody = {
      message: `Menu ${newValid ? 'Activated' : 'Deactivated'}`,
      summary: JSON.stringify([
        `Menu "${nodeData?.title || 'Untitled Menu'}" ${newValid ? 'activated' : 'deactivated'}`,
        `Menu ID: ${nodeData?.id || ''}`,
        `Previous status: ${valid ? 'Active' : 'Inactive'}`,
        `New status: ${newValid ? 'Active' : 'Inactive'}`,
        `Location ID: ${match.params.locationId}`,
      ]),
      type: "menu_status_change",
      code: newValid ? "activate" : "deactivate",
      access_type: true,
      metadata: {
        changedBy: "",
        menuId: nodeData?.id || "",
        menuTitle: nodeData?.title || "",
        previousStatus: valid,
        newStatus: newValid,
        locationId: match.params.locationId,
      },
    };

    try {
      console.log("Deactivating menu:", nodeData.id, "new valid:", newValid);
      await dispatch(updateMenu(
        match.params.locationId, 
        cloneData,
        logBody,
        "menu"
      ));
      // ✅ Refresh after update
      await dispatch(fetchMenus(match.params.locationId));
      await dispatch(fetchAllMenuDetails(match.params.locationId));
    } catch (err) {
      console.log("Error updating menu:", err);
      setError(ERROR_MESSAGE_UNEXPECTED_ERROR);
    } finally {
      setUpdatingMenuId(null);
    }
  };

  const handleOpenMenuAddModal = () => setIsOpenMenuAddModal(true);
  const handleCloseMenuAddModal = () => {
    setIsOpenMenuAddModal(false);
    setMenuName("");
    // ✅ Refresh after adding
    dispatch(fetchMenus(match.params.locationId));
    dispatch(fetchAllMenuDetails(match.params.locationId));
  };

  const handleOpenMenuEditModal = (e: React.MouseEvent, menuDetails: any) => {
    e.stopPropagation();
    setIsOpenMenuEditModal(true);
    setMenuName(menuDetails.name);
    setMenu(menuDetails);
    setType(menuDetails.type);
    setBrand(menuDetails.brand);
    setTypeInitial(menuDetails.type);
    setBrandInitial(menuDetails.brand);
    setMenuNameInitial(menuDetails.name);
  };

  const handleCloseMenuEditModal = () => {
    setIsOpenMenuEditModal(false);
    setMenuName("");
    setType("");
    setBrand("");
    // ✅ Refresh after editing
    dispatch(fetchMenus(match.params.locationId));
    dispatch(fetchAllMenuDetails(match.params.locationId));
  };

  const renderMenusList = () => {
    console.log("Rendering menus list, menus length:", menus?.length);

    if ((!menus || menus.length === 0) && !isLoading) {
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
        >
          <Typography variant="h6" color="textSecondary">
            No menus available. Click "Add New Menu" to create one.
          </Typography>
        </Box>
      );
    }

    if (!menus || menus.length === 0) {
      return null;
    }

    return (
      <Box style={{ padding: "16px 24px" }} key={refreshTrigger}>
        <Box display="flex" justifyContent="flex-end" mb={2}>
          <Button
            variant="contained"
            style={{
              backgroundColor: theme.palette.custom.yellow.main,
              color: theme.palette.custom.yellow.contrastText,
              borderRadius: "10px",
            }}
            onClick={handleOpenMenuAddModal}
            startIcon={<AddIcon />}
            disabled={isEditDisabled}
          >
            Add New Menu
          </Button>
        </Box>

        <List
          style={{
            backgroundColor: theme.palette.background.entity_background,
            borderRadius: "12px",
            overflow: "hidden",
          }}
        >
          {menus.map((menu: any, index: number) => {
            const stats = categoriesInfo[menu.id] || {
              categoryCount: 0,
              itemCount: 0,
            };
            const isUpdating = updatingMenuId === menu.id;
            const uniqueKey = `${menu.id}_${menu.valid}_${menu.version}_${refreshTrigger}`;
            return (
              <React.Fragment key={uniqueKey}>
                {index > 0 && (
                  <Divider
                    style={{
                      backgroundColor: theme.palette.background.entity_border,
                    }}
                  />
                )}
                <ListItem
                  style={{
                    padding: "16px 24px",
                    backgroundColor: theme.palette.background.entity_background,
                    cursor: "pointer",
                  }}
                  onClick={(e) => handleOpenMenuEditModal(e, menu)}
                >
                  <ListItemAvatar style={{ marginRight: "12px" }}>
                    <Avatar
                      variant="rounded"
                      style={{
                        width: 60,
                        height: 60,
                        backgroundColor: theme.palette.background.imgBackground,
                      }}
                    >
                      {menu.img ? (
                        <img
                          src={menu.img}
                          alt={menu.name}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      ) : (
                        <ImageOutlinedIcon
                          style={{
                            fontSize: 40,
                            color: theme.palette.text.secondary,
                          }}
                        />
                      )}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Typography
                        variant="h6"
                        style={{ fontWeight: "bold", marginBottom: "4px" }}
                      >
                        {menu.name}
                      </Typography>
                    }
                    secondary={
                      <>
                        {menu.desc && (
                          <Typography
                            variant="body2"
                            color="textSecondary"
                            style={{ marginBottom: "8px" }}
                          >
                            {menu.desc}
                          </Typography>
                        )}
                        <Box display="flex" style={{ gap: 3 }}>
                          <Box
                            display="flex"
                            alignItems="center"
                            style={{ gap: 0.5 }}
                          >
                            <Icon icon="fluent:layer-24-filled" width={14} />
                            <Typography variant="caption" color="textSecondary">
                              {stats.categoryCount} Categories
                            </Typography>
                          </Box>
                          <Box
                            display="flex"
                            alignItems="center"
                            style={{ gap: 0.5 }}
                          >
                            <Icon icon="lucide:package" width={14} />
                            <Typography variant="caption" color="textSecondary">
                              {stats.itemCount} Items
                            </Typography>
                          </Box>
                          {!menu.valid && (
                            <Box
                              display="flex"
                              alignItems="center"
                              style={{ gap: 0.5 }}
                            >
                              <Icon
                                icon="lucide:alert-circle"
                                width={14}
                                color={theme.palette.custom.red.main}
                              />
                              <Typography
                                variant="caption"
                                style={{ color: theme.palette.custom.red.main }}
                              >
                                Inactive
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </>
                    }
                  />
                  <Box display="flex" style={{ gap: 1 }} alignItems="center">
                    <div onClick={(e) => e.stopPropagation()}>
                      <Button
                        size="small"
                        variant="contained"
                        style={{
                          backgroundColor: theme.palette.custom.blue.main,
                          color: theme.palette.custom.blue.contrastText,
                          minWidth: "80px",
                          marginRight: "12px",
                          borderRadius: "10px",
                        }}
                        onClick={(e) => handleViewMenu(e, menu.id, menu.name)}
                        startIcon={<VisibilityIcon />}
                      >
                        View
                      </Button>
                      <ButtonCommon
                        isLoadingPage={isUpdating}
                        variant="contained"
                        style={{
                          fontSize: 11,
                          minWidth: "80px",
                          height: "34px",
                          borderRadius: "10px",
                        }}
                        color={menu.valid ? "red" : "green"}
                        onClick={(e: any) =>
                          handleDeactivateMenu(menu, menu.valid)
                        }
                        disabled={isEditDisabled}
                      >
                        {menu.valid ? "Deactivate" : "Activate"}
                      </ButtonCommon>
                    </div>
                  </Box>
                </ListItem>
              </React.Fragment>
            );
          })}
        </List>
      </Box>
    );
  };

  return (
    <>
      <Hidden lgUp>
        <PageHeaderMobile />
      </Hidden>
      <Box style={{ marginTop: "18px" }}>
        {isLoading && (!menus || menus.length === 0) ? (
          <MenuListSkeletonLoading />
        ) : (
          renderMenusList()
        )}
      </Box>
      <MenuAddModal
        isOpen={isOpenMenuAddModal}
        setIsOpen={setIsOpenMenuAddModal}
        setMenuName={setMenuName}
        menuName={menuName}
        isMenuNameEmpty={isMenuNameEmpty}
        setIsMenuNameEmpty={setIsMenuNameEmpty}
        handleCloseMenuAddModal={handleCloseMenuAddModal}
        isLoadingButton={isLoadingButton}
        setIsLoadingButton={setIsLoadingButton}
        setSelectedMenuId={() => {}}
        setError={setError}
        menuNodeList={menus}
        setMenuNodeList={() => {}}
      />
      <MenuEditModal
        isOpen={isOpenMenuEditModal}
        setIsOpen={setIsOpenMenuEditModal}
        setMenuName={setMenuName}
        menuName={menuName}
        isMenuNameEmpty={isMenuNameEmpty}
        setIsMenuNameEmpty={setIsMenuNameEmpty}
        handleCloseMenuEditModal={handleCloseMenuEditModal}
        isLoadingButton={isLoadingButton}
        setType={setType}
        type={type}
        brand={brand}
        setBrand={setBrand}
        typeInitial={typeInitial}
        brandInitial={brandInitial}
        menuNameInitial={menuNameInitial}
        menu={menu}
        setError={setError}
        setIsLoadingButton={setIsLoadingButton}
        menuNodeList={menus}
        setMenuNodeList={() => {}}
        isEditDisabled={isEditDisabled}
      />
      <DefaultAlert
        open={!!error}
        handleClose={() => setError("")}
        message={error}
        severity="error"
      />
    </>
  );
};

export default withAuthority(MenuList, Authorities.DISCOUNT_READ);